/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 27/09/22
 */

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { Dialog, DialogProps } from '@/components/Dialog';
import { LabeledSelect } from '@/components/FormInputField';
import { Workplan } from '@/wpt-lib/interfaces/Workplan';
import { usePostRequest } from '@/utils/useStatefulRequests';
import { Text } from '@chakra-ui/react';
import * as React from 'react';
import { useCallback, useLayoutEffect, useState } from 'react';

interface CreateFutureWorkplanDialog extends Pick<DialogProps, 'onClose' | 'isOpen'> {
    years: string[];
    onCreate: (workplan: Workplan) => void;
}

const CreateFutureWorkplanDialog = ({ years, isOpen, onClose, onCreate }: CreateFutureWorkplanDialog) => {
    const { currentAssignment } = useAssignment();
    const { makeRequest: makeCreateRequest, loading: creatingWorkplan } = usePostRequest<Workplan, undefined>();
    const [createErrorStr, setCreateErrorStr] = useState<string>();

    const [yearToCreate, setYearToCreate] = useState<string | undefined>(undefined);
    useLayoutEffect(() => {
        if (years.length) {
            let defaultOpt = years[0];
            const current = new Date().getFullYear();
            for (const year of years) {
                if (+year >= current) {
                    defaultOpt = year;
                    break;
                }
            }
            setYearToCreate(defaultOpt);
        }
    }, [years]);

    const handleCreate = useCallback(async () => {
        setCreateErrorStr(undefined);
        try {
            const response = await makeCreateRequest(`/workplan/year/${yearToCreate}`, undefined, {
                params: { assignment: currentAssignment?.id },
            });
            if (onCreate) onCreate(response.data);
        } catch (e) {
            setCreateErrorStr(`Could not create ${yearToCreate} Workplan`);
        }
    }, [yearToCreate, currentAssignment]);

    return (
        <Dialog
            dialogHeader="Add Workplan for Year"
            isOpen={isOpen}
            isLoading={creatingWorkplan}
            onClose={onClose}
            errorMsg={createErrorStr}
            dialogAction={handleCreate}
            dialogConfirmLabel={'Add'}
        >
            <>
                <Text>Select the year you want to create a workplan for.</Text>
                <Text fontStyle="italic">
                    Note: you can also copy an existing workplan by selecting the &quot;Copy Workplan&quot; option if
                    you want to quickly copy entries.
                </Text>
                <LabeledSelect
                    label="Select year:"
                    value={yearToCreate}
                    onChange={(e) => setYearToCreate(e.target.value)}
                    isDisabled={creatingWorkplan}
                >
                    {years.map((y) => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </LabeledSelect>
            </>
        </Dialog>
    );
};

export default CreateFutureWorkplanDialog;
